import React, { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import SendIcon from "@material-ui/icons/Send";
import SearchIcon from "@material-ui/icons/Search";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	getStoredPrivacyCensorSettings,
	privacyCensorChangedEvent,
} from "../../utils/privacyCensor";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import WasheetsPanel from "../WasheetsPanel";
import CatalogSheetsPanel from "../CatalogSheetsPanel";
import ChatbotModal from "../ChatbotModal";

const drawerWidth = 360;
const LABEL_FIELD_PREFIX = "Etiquetas-";

const isLabelExtraInfo = infoOrName => {
	const name = typeof infoOrName === "string" ? infoOrName : infoOrName?.name;
	return String(name || "").startsWith(LABEL_FIELD_PREFIX);
};

const getContactFileUrl = fileUrl => {
	const value = String(fileUrl || "").trim();
	if (!value) return "";
	if (/^https?:\/\//i.test(value)) return value;
	const baseUrl = String(getBackendUrl() || "").replace(/\/+$/, "");
	const path = value.replace(/^\/+/, "");
	return `${baseUrl}/${path}`;
};

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerClosed: {
		pointerEvents: "none",
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	drawerPaperClosed: {
		pointerEvents: "none",
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	headerTitle: {
		flex: 1,
		textAlign: "center",
		marginRight: theme.spacing(5),
	},
	tabBar: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1),
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.paper,
	},
	tabButton: {
		width: 38,
		height: 38,
		borderRadius: 10,
		color: theme.palette.primary.main,
		backgroundColor: theme.palette.action.hover,
		transition: "background-color 160ms ease, color 160ms ease",
		"&:hover": {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
		},
	},
	tabButtonActive: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	tabContent: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},
	censoredPhoto: {
		filter: "blur(10px)",
		transform: "scale(1.04)",
		borderRadius: "50%",
		overflow: "hidden",
	},
	censoredText: {
		padding: "1px 8px",
		filter: "blur(3px)",
		userSelect: "none",
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
	contactFileLink: {
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		maxWidth: "100%",
		marginTop: theme.spacing(0.5),
	},
	whatsappWarning: {
		width: "100%",
		boxSizing: "border-box",
		marginTop: 8,
		padding: theme.spacing(1),
		border: "1px solid #f0b429",
		borderRadius: 4,
		backgroundColor: "#fff8e1",
		color: "#7a4f01",
		textAlign: "left",
	},
	sequencesPanel: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	sequencesToolbar: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
	},
	searchField: {
		flex: 1,
		backgroundColor: theme.palette.background.paper,
	},
	chipRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.75),
	},
	sequenceGroup: {
		borderRadius: 8,
		overflow: "hidden",
		border: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.paper,
		position: "relative",
	},
	sequenceGroupDropBefore: {
		"&:before": {
			backgroundColor: theme.palette.primary.main,
			borderRadius: 999,
			boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
			content: '""',
			height: 4,
			left: 0,
			position: "absolute",
			right: 0,
			top: -2,
			zIndex: 3,
		},
	},
	sequenceGroupDropAfter: {
		"&:after": {
			backgroundColor: theme.palette.primary.main,
			borderRadius: 999,
			bottom: -2,
			boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
			content: '""',
			height: 4,
			left: 0,
			position: "absolute",
			right: 0,
			zIndex: 3,
		},
	},
	sequenceGroupHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(0.75, 1),
		cursor: "pointer",
	},
	sequenceGroupTitle: {
		minWidth: 0,
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		fontWeight: 600,
	},
	sequenceDot: {
		width: 10,
		height: 10,
		borderRadius: 10,
		flexShrink: 0,
	},
	sequenceList: {
		display: "flex",
		flexDirection: "column",
		padding: theme.spacing(0, 1, 1, 2),
	},
	sequenceItem: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(0.75, 0),
		borderTop: "1px solid rgba(0, 0, 0, 0.08)",
	},
	sequenceItemDragging: {
		opacity: 0.55,
	},
	sequenceItemDropBefore: {
		boxShadow: `inset 0 3px 0 ${theme.palette.primary.main}`,
	},
	sequenceItemDropAfter: {
		boxShadow: `inset 0 -3px 0 ${theme.palette.primary.main}`,
	},
	dragHandle: {
		color: theme.palette.text.secondary,
		cursor: "grab",
		display: "inline-flex",
		flexShrink: 0,
		"&:active": {
			cursor: "grabbing",
		},
	},
	dragHandleDisabled: {
		cursor: "not-allowed",
		opacity: 0.36,
	},
	sequenceItemText: {
		minWidth: 0,
		flex: 1,
	},
	sequenceName: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	sequenceMeta: {
		color: theme.palette.text.secondary,
		fontSize: 12,
	},
	emptyState: {
		padding: theme.spacing(2),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
}));

const tabs = [
	{ value: "contact", label: "Contacto", icon: <PersonOutlineIcon /> },
	{ value: "forms", label: "Formularios", icon: <AssignmentOutlinedIcon /> },
	{ value: "catalog", label: "Catalogo", icon: <StorefrontOutlinedIcon /> },
	{ value: "sequences", label: "Secuencias", icon: <FlashOnIcon /> },
];

const sequenceColors = ["#2F80ED", "#27AE60", "#F2994A", "#9B51E0", "#EB5757", "#00A3A3"];
const CATEGORY_DRAG_TYPE = "text/contact-drawer-chatbot-category";
const SEQUENCE_DRAG_TYPE = "text/contact-drawer-chatbot-sequence";

const getSequenceColor = index => sequenceColors[index % sequenceColors.length];

const compareSequences = (left, right) =>
	Number(left.position || 0) - Number(right.position || 0) ||
	String(left.name || "").localeCompare(String(right.name || "")) ||
	Number(left.id || 0) - Number(right.id || 0);

const getOrderedCategories = sequences => {
	const categoryMap = new Map();

	(sequences || []).forEach(sequence => {
		const category = sequence.category || "General";
		const current = categoryMap.get(category);
		if (
			!current ||
			Number(sequence.categoryPosition || 0) <
				Number(current.categoryPosition || 0)
		) {
			categoryMap.set(category, {
				category,
				categoryPosition: Number(sequence.categoryPosition || 0),
			});
		}
	});

	return Array.from(categoryMap.values())
		.sort(
			(left, right) =>
				Number(left.categoryPosition || 0) -
					Number(right.categoryPosition || 0) ||
				String(left.category || "").localeCompare(String(right.category || ""))
		)
		.map(item => item.category);
};

const buildReorderPayload = sequences =>
	getOrderedCategories(sequences).map((category, categoryIndex) => ({
		category,
		position: categoryIndex,
		sequenceIds: sequences
			.filter(sequence => (sequence.category || "General") === category)
			.sort(compareSequences)
			.map(sequence => sequence.id),
	}));

const ContactDrawer = ({
	open,
	handleDrawerClose,
	contact,
	ticket,
	loading,
	initialTab = "contact",
}) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);

	const [modalOpen, setModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("contact");
	const [sequences, setSequences] = useState([]);
	const [sequenceSearch, setSequenceSearch] = useState("");
	const [sequenceCategory, setSequenceCategory] = useState("all");
	const [expandedGroups, setExpandedGroups] = useState({});
	const [loadingSequences, setLoadingSequences] = useState(false);
	const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
	const [sequenceModalId, setSequenceModalId] = useState(null);
	const [draggingCategory, setDraggingCategory] = useState("");
	const [draggingSequenceId, setDraggingSequenceId] = useState("");
	const [categoryDropTarget, setCategoryDropTarget] = useState(null);
	const [sequenceDropTarget, setSequenceDropTarget] = useState(null);
	const [privacyCensorSettings, setPrivacyCensorSettings] = useState(() =>
		getStoredPrivacyCensorSettings(user?.id)
	);

	useEffect(() => {
		setPrivacyCensorSettings(getStoredPrivacyCensorSettings(user?.id));
	}, [user?.id]);

	useEffect(() => {
		const handlePrivacySettingsChanged = event => {
			setPrivacyCensorSettings(
				event?.detail || getStoredPrivacyCensorSettings(user?.id)
			);
		};

		window.addEventListener(
			privacyCensorChangedEvent,
			handlePrivacySettingsChanged
		);

		return () => {
			window.removeEventListener(
				privacyCensorChangedEvent,
				handlePrivacySettingsChanged
			);
		};
	}, [user?.id]);
	const [sendingSequenceId, setSendingSequenceId] = useState(null);

	const loadSequences = async () => {
		setLoadingSequences(true);
		try {
			const { data } = await api.get("/chatbot/sequences", {
				params: { all: true, searchParam: "", pageNumber: 1 },
			});
			const nextSequences = data.sequences || [];
			setSequences(nextSequences);
			setExpandedGroups(prevState => {
				if (Object.keys(prevState).length) return prevState;
				return nextSequences.reduce((acc, sequence) => {
					acc[sequence.category || "General"] = true;
					return acc;
				}, {});
			});
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingSequences(false);
		}
	};

	useEffect(() => {
		if (!open || activeTab !== "sequences") return;
		loadSequences();
	}, [open, activeTab]);

	useEffect(() => {
		if (open && initialTab) {
			setActiveTab(initialTab);
		}
	}, [open, initialTab]);

	const sequenceCategories = useMemo(
		() => getOrderedCategories(sequences),
		[sequences]
	);

	const groupedSequences = useMemo(() => {
		const search = sequenceSearch.trim().toLowerCase();
		const grouped = sequences
			.filter(sequence => {
				const category = sequence.category || "General";
				if (sequenceCategory !== "all" && category !== sequenceCategory) return false;
				if (!search) return true;
				return `${sequence.name || ""} ${sequence.trigger || ""} ${category}`
					.toLowerCase()
					.includes(search);
			})
			.reduce((acc, sequence) => {
				const category = sequence.category || "General";
				if (!acc[category]) acc[category] = [];
				acc[category].push(sequence);
				return acc;
			}, {});

		Object.keys(grouped).forEach(category => {
			grouped[category] = grouped[category].sort(compareSequences);
		});

		return grouped;
	}, [sequences, sequenceSearch, sequenceCategory]);

	const openCreateSequence = () => {
		setSequenceModalId(null);
		setSequenceModalOpen(true);
	};

	const openEditSequence = sequenceId => {
		setSequenceModalId(sequenceId);
		setSequenceModalOpen(true);
	};

	const handleSendSequence = async sequenceId => {
		if (!ticket?.id || sendingSequenceId) return;

		setSendingSequenceId(sequenceId);
		try {
			await api.post(`/chatbot/sequences/${sequenceId}/send/${ticket.id}`);
			toast.success("Secuencia enviada.");
		} catch (err) {
			toastError(err);
		} finally {
			setSendingSequenceId(null);
		}
	};

	const handleSequenceSaved = savedSequence => {
		setSequenceModalOpen(false);
		loadSequences();
		if (savedSequence?.category && sequenceCategory !== "all") {
			setSequenceCategory(savedSequence.category);
		}
	};

	const canReorderSequences = !sequenceSearch.trim();
	const canReorderCategories =
		canReorderSequences && sequenceCategory === "all";

	const syncReorderedSequences = async nextSequences => {
		setSequences(nextSequences);
		try {
			const { data } = await api.put("/chatbot/sequences/reorder", {
				categories: buildReorderPayload(nextSequences),
			});
			if (Array.isArray(data.sequences)) setSequences(data.sequences);
		} catch (err) {
			toastError(err);
			loadSequences();
		}
	};

	const moveCategory = (sourceCategory, targetCategory, placement = "after") => {
		if (!canReorderCategories || !sourceCategory || !targetCategory) return;
		if (sourceCategory === targetCategory) return;

		const withoutSource = sequenceCategories.filter(
			category => category !== sourceCategory
		);
		const targetIndex = withoutSource.findIndex(
			category => category === targetCategory
		);
		const insertIndex =
			targetIndex >= 0
				? targetIndex + (placement === "after" ? 1 : 0)
				: withoutSource.length;
		withoutSource.splice(insertIndex, 0, sourceCategory);

		const categoryPositionByName = new Map(
			withoutSource.map((category, index) => [category, index])
		);
		syncReorderedSequences(
			sequences.map(sequence => ({
				...sequence,
				categoryPosition:
					categoryPositionByName.get(sequence.category || "General") ??
					sequence.categoryPosition,
			}))
		);
		setDraggingCategory("");
		setCategoryDropTarget(null);
	};

	const moveSequence = (sourceId, targetId, category, placement = "after") => {
		if (!canReorderSequences || !sourceId || !targetId || !category) return;
		if (String(sourceId) === String(targetId)) return;

		const categorySequences = sequences
			.filter(sequence => (sequence.category || "General") === category)
			.sort(compareSequences);
		const draggedSequence = categorySequences.find(
			sequence => String(sequence.id) === String(sourceId)
		);
		if (!draggedSequence) return;

		const withoutSource = categorySequences.filter(
			sequence => String(sequence.id) !== String(sourceId)
		);
		const targetIndex = withoutSource.findIndex(
			sequence => String(sequence.id) === String(targetId)
		);
		const insertIndex =
			targetIndex >= 0
				? targetIndex + (placement === "after" ? 1 : 0)
				: withoutSource.length;
		withoutSource.splice(insertIndex, 0, draggedSequence);

		const positionById = new Map(
			withoutSource.map((sequence, index) => [String(sequence.id), index])
		);
		syncReorderedSequences(
			sequences.map(sequence =>
				(sequence.category || "General") === category
					? {
							...sequence,
							position:
								positionById.get(String(sequence.id)) ?? sequence.position,
					  }
					: sequence
			)
		);
		setDraggingSequenceId("");
		setSequenceDropTarget(null);
	};

	const renderContactTab = () => (
		<>
			<Paper square variant="outlined" className={classes.contactHeader}>
				<Avatar
					alt={
						privacyCensorSettings.contactNames ? "contact_image" : contact.name
					}
					src={
						privacyCensorSettings.contactPhotos
							? undefined
							: contact.profilePicUrl
					}
					className={clsx(classes.contactAvatar, {
						[classes.censoredPhoto]: privacyCensorSettings.contactPhotos,
					})}
				></Avatar>

				<Typography>
					<span
						className={clsx({
							[classes.censoredText]: privacyCensorSettings.contactNames,
						})}
					>
						{privacyCensorSettings.contactNames
							? "Contacto privado"
							: contact.name}
					</span>
				</Typography>
				{contact.number && (
					<Typography>
						{privacyCensorSettings.contactNames ? (
							<span className={classes.censoredText}>Telefono privado</span>
						) : (
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						)}
					</Typography>
				)}
				{contact.chatId && (
					<Typography variant="body2" color="textSecondary">
						{privacyCensorSettings.contactNames ? (
							<span className={classes.censoredText}>ChatId privado</span>
						) : (
							<>ChatId: {contact.chatId}</>
						)}
					</Typography>
				)}
				{contact.whatsappStatus === "invalid" && (
					<Typography variant="body2" className={classes.whatsappWarning}>
						{i18n.t("contactDrawer.whatsapp.invalid")}
					</Typography>
				)}
				{contact.whatsappStatus === "pending" && !contact.chatId && (
					<Typography variant="body2" className={classes.whatsappWarning}>
						{i18n.t("contactDrawer.whatsapp.pending")}
					</Typography>
				)}
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setModalOpen(true)}
				>
					{i18n.t("contactDrawer.buttons.edit")}
				</Button>
			</Paper>
			<Paper square variant="outlined" className={classes.contactDetails}>
				<Typography variant="subtitle1">
					{i18n.t("contactDrawer.extraInfo")}
				</Typography>
				{contact?.extraInfo?.filter(info => !isLabelExtraInfo(info)).map(info => (
					<Paper
						key={info.id}
						square
						variant="outlined"
						className={classes.contactExtraInfo}
					>
						<InputLabel>{info.name}</InputLabel>
						<Typography
							component="div"
							noWrap
							style={{ paddingTop: 2 }}
							className={clsx({
								[classes.censoredText]: privacyCensorSettings.contactNames,
							})}
						>
							{privacyCensorSettings.contactNames ? (
								"Dato privado"
							) : info.fieldType === "file" || info.fileUrl ? (
								<Link
									className={classes.contactFileLink}
									href={getContactFileUrl(info.fileUrl || info.value)}
									target="_blank"
									rel="noopener noreferrer"
									title={info.fileName || info.value || "Archivo"}
								>
									<AttachFileIcon fontSize="small" />
									<span>{info.fileName || info.name || "Archivo"}</span>
								</Link>
							) : (
								<MarkdownWrapper>{info.value}</MarkdownWrapper>
							)}
						</Typography>
					</Paper>
				))}
			</Paper>
		</>
	);

	const renderSequencesTab = () => {
		const groupEntries = Object.entries(groupedSequences);

		return (
			<div className={classes.sequencesPanel}>
				<div className={classes.sequencesToolbar}>
					<TextField
						className={classes.searchField}
						value={sequenceSearch}
						onChange={event => setSequenceSearch(event.target.value)}
						placeholder="Buscar secuencia"
						variant="outlined"
						margin="dense"
						InputProps={{
							startAdornment: <SearchIcon fontSize="small" color="disabled" />,
						}}
					/>
					<Tooltip title="Crear secuencia">
						<IconButton color="primary" onClick={openCreateSequence}>
							<AddIcon />
						</IconButton>
					</Tooltip>
				</div>
				<div className={classes.chipRow}>
					<Chip
						size="small"
						label="Todas"
						color={sequenceCategory === "all" ? "primary" : "default"}
						onClick={() => setSequenceCategory("all")}
					/>
					{sequenceCategories.map(category => (
						<Chip
							key={category}
							size="small"
							label={category}
							color={sequenceCategory === category ? "primary" : "default"}
							onClick={() => setSequenceCategory(category)}
						/>
					))}
				</div>
				{loadingSequences ? (
					<div className={classes.emptyState}>
						<CircularProgress size={24} />
					</div>
				) : !groupEntries.length ? (
					<Paper square variant="outlined" className={classes.emptyState}>
						No hay secuencias para mostrar.
					</Paper>
				) : (
					groupEntries.map(([category, items], index) => {
						const expanded = expandedGroups[category] !== false;
						const color = getSequenceColor(index);

						return (
							<Paper
								key={category}
								elevation={0}
								className={clsx(classes.sequenceGroup, {
									[classes.sequenceGroupDropBefore]:
										categoryDropTarget?.category === category &&
										categoryDropTarget?.placement === "before",
									[classes.sequenceGroupDropAfter]:
										categoryDropTarget?.category === category &&
										categoryDropTarget?.placement === "after",
								})}
								onDragOver={event => {
									if (
										!canReorderCategories ||
										!draggingCategory ||
										draggingCategory === category
									) {
										return;
									}
									event.preventDefault();
									event.stopPropagation();
									const bounds = event.currentTarget.getBoundingClientRect();
									const placement =
										event.clientY > bounds.top + bounds.height / 2
											? "after"
											: "before";
									const nextTarget = { category, placement };
									setCategoryDropTarget(prevState =>
										prevState?.category === nextTarget.category &&
										prevState?.placement === nextTarget.placement
											? prevState
											: nextTarget
									);
								}}
								onDrop={event => {
									if (!canReorderCategories || !draggingCategory) return;
									event.preventDefault();
									event.stopPropagation();
									moveCategory(
										draggingCategory,
										category,
										categoryDropTarget?.placement || "after"
									);
								}}
							>
								<div
									className={classes.sequenceGroupHeader}
									onClick={() =>
										setExpandedGroups(prevState => ({
											...prevState,
											[category]: !expanded,
										}))
									}
								>
									<div className={classes.sequenceGroupTitle}>
										<Tooltip
											title={
												canReorderCategories
													? "Arrastrar categoría"
													: "Quita la búsqueda y muestra todas para ordenar categorías"
											}
										>
											<span
												className={clsx(classes.dragHandle, {
													[classes.dragHandleDisabled]: !canReorderCategories,
												})}
												draggable={canReorderCategories}
												onClick={event => event.stopPropagation()}
												onDragStart={event => {
													if (!canReorderCategories) return;
													event.stopPropagation();
													event.dataTransfer.setData(CATEGORY_DRAG_TYPE, category);
													event.dataTransfer.effectAllowed = "move";
													setDraggingCategory(category);
												}}
												onDragEnd={() => {
													setDraggingCategory("");
													setCategoryDropTarget(null);
												}}
											>
												<DragIndicatorIcon fontSize="small" />
											</span>
										</Tooltip>
										<span
											className={classes.sequenceDot}
											style={{ backgroundColor: color }}
										/>
										<Typography noWrap variant="subtitle2">
											{category}
										</Typography>
										<Chip size="small" label={items.length} />
									</div>
									{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								</div>
								<Collapse in={expanded}>
									<div className={classes.sequenceList}>
										{items.map(sequence => (
											<div
												key={sequence.id}
												className={clsx(classes.sequenceItem, {
													[classes.sequenceItemDragging]:
														String(draggingSequenceId) === String(sequence.id),
													[classes.sequenceItemDropBefore]:
														sequenceDropTarget?.sequenceId ===
															String(sequence.id) &&
														sequenceDropTarget?.placement === "before",
													[classes.sequenceItemDropAfter]:
														sequenceDropTarget?.sequenceId ===
															String(sequence.id) &&
														sequenceDropTarget?.placement === "after",
												})}
												onDragOver={event => {
													if (
														!canReorderSequences ||
														!draggingSequenceId ||
														String(draggingSequenceId) === String(sequence.id)
													) {
														return;
													}
													event.preventDefault();
													event.stopPropagation();
													const bounds =
														event.currentTarget.getBoundingClientRect();
													const placement =
														event.clientY > bounds.top + bounds.height / 2
															? "after"
															: "before";
													const nextTarget = {
														category,
														sequenceId: String(sequence.id),
														placement,
													};
													setSequenceDropTarget(prevState =>
														prevState?.category === nextTarget.category &&
														prevState?.sequenceId ===
															nextTarget.sequenceId &&
														prevState?.placement === nextTarget.placement
															? prevState
															: nextTarget
													);
												}}
												onDrop={event => {
													if (!canReorderSequences || !draggingSequenceId) {
														return;
													}
													event.preventDefault();
													event.stopPropagation();
													moveSequence(
														draggingSequenceId,
														sequence.id,
														category,
														sequenceDropTarget?.placement || "after"
													);
												}}
											>
												<Tooltip
													title={
														canReorderSequences
															? "Arrastrar secuencia"
															: "Quita la búsqueda para ordenar secuencias"
													}
												>
													<span
														className={clsx(classes.dragHandle, {
															[classes.dragHandleDisabled]:
																!canReorderSequences,
														})}
														draggable={canReorderSequences}
														onDragStart={event => {
															if (!canReorderSequences) return;
															event.dataTransfer.setData(
																SEQUENCE_DRAG_TYPE,
																String(sequence.id)
															);
															event.dataTransfer.effectAllowed = "move";
															setDraggingSequenceId(String(sequence.id));
														}}
														onDragEnd={() => {
															setDraggingSequenceId("");
															setSequenceDropTarget(null);
														}}
													>
														<DragIndicatorIcon fontSize="small" />
													</span>
												</Tooltip>
												<div className={classes.sequenceItemText}>
													<Typography className={classes.sequenceName}>
														{sequence.name}
													</Typography>
													<Typography className={classes.sequenceMeta}>
														ID {sequence.id}
														{sequence.trigger ? ` - ${sequence.trigger}` : ""}
													</Typography>
												</div>
												<div>
													<Tooltip title="Enviar secuencia">
														<IconButton
															size="small"
															disabled={!ticket?.id || sendingSequenceId === sequence.id}
															onClick={() => handleSendSequence(sequence.id)}
														>
															{sendingSequenceId === sequence.id ? (
																<CircularProgress size={16} />
															) : (
																<SendIcon fontSize="small" />
															)}
														</IconButton>
													</Tooltip>
													<Tooltip title="Editar secuencia">
														<IconButton
															size="small"
															onClick={() => openEditSequence(sequence.id)}
														>
															<EditIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</div>
											</div>
										))}
									</div>
								</Collapse>
							</Paper>
						);
					})
				)}
			</div>
		);
	};

	return (
		<Drawer
			className={clsx(classes.drawer, {
				[classes.drawerClosed]: !open,
			})}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: clsx(classes.drawerPaper, {
					[classes.drawerPaperClosed]: !open,
				}),
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography className={classes.headerTitle}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<>
					<ContactModal
						open={modalOpen}
						onClose={() => setModalOpen(false)}
						contactId={contact.id}
					></ContactModal>
					<ChatbotModal
						open={sequenceModalOpen}
						onClose={() => setSequenceModalOpen(false)}
						onSave={handleSequenceSaved}
						sequenceId={sequenceModalId}
						defaultConnectionId={ticket?.connectionId || ""}
						categories={sequenceCategories}
					/>
					<div className={classes.tabBar}>
						{tabs.map(tab => (
							<Tooltip key={tab.value} title={tab.label}>
								<IconButton
									className={`${classes.tabButton} ${
										activeTab === tab.value ? classes.tabButtonActive : ""
									}`}
									onClick={() => setActiveTab(tab.value)}
								>
									{tab.icon}
								</IconButton>
							</Tooltip>
						))}
					</div>
					<div className={classes.tabContent}>
						{activeTab === "contact" && renderContactTab()}
						{activeTab === "forms" && <WasheetsPanel contact={contact} />}
						{activeTab === "catalog" && <CatalogSheetsPanel />}
						{activeTab === "sequences" && renderSequencesTab()}
					</div>
				</>
			)}
		</Drawer>
	);
};

export default ContactDrawer;

